import type { Instance } from "@gadgetinc/mobx-quick-tree";
import type { DeepReadonly } from "ts-essentials";
import type { ModelField } from "../database/ModelField";
import type { LiveOrReadOnlyServerRoot } from "../LiveOrServerRoot";
import { FieldType } from "../type-system/FieldType";
import { getAppSettings, getGeneratingRoot } from "../utils";
import {
  ShopifyAppSubscriptionKey,
  ShopifyCollectionKey,
  ShopifyCompanyContactKey,
  ShopifyCompanyKey,
  ShopifyCompanyLocationKey,
  ShopifyCustomerKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyDiscountKey,
  ShopifyDiscountRedeemCodeKey,
  ShopifyDiscountToDiscountRedeemCodesKey,
  ShopifyFulfillmentOrderKey,
  ShopifyFulfillmentOrderLineItemKey,
  ShopifyFulfillmentOrderToFulfillmentOrderLineItemsKey,
  ShopifyMarketKey,
  ShopifyMarketRegionKey,
  ShopifyMarketWebPresenceKey,
  ShopifyOrderKey,
  ShopifyPaymentTermsKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
  ShopifySellingPlanGroupKey,
  ShopifyShopKey,
  ShopifySubscriptionContractKey,
} from "./shopify/keys";
import type { ShopifyApiVersion } from "./shopify/ShopifyApiVersion";
import { ShopifyDominion } from "./shopify/ShopifyDominion";
import { getRootWebhookModelKey, shopifyModelMetadataForVersion, upsertWebhookTopicsForModelKey } from "./shopify/ShopifyModelMetadata";
import { isWebhookTriggeredModelKey } from "./shopify/sync-only";
import type { ShopifyConnectionConfig } from "./ShopifyConnectionConfig";
import type { ConnectionModelFieldSpec } from "./utils";

type FieldLike =
  | ModelField
  | {
      modelKey: string;
      apiVersion: ShopifyApiVersion;
      frameworkVersionConstraint: string;
      fieldSpec: ConnectionModelFieldSpec | DeepReadonly<ConnectionModelFieldSpec>;
    };

const isBelongsToShopModelField = (field: ModelField | DeepReadonly<ConnectionModelFieldSpec>) => {
  return field.configuration?.relatedModelKey === ShopifyShopKey;
};

const isBelongsToParentWebhookModel = (field: FieldLike): boolean => {
  if ("fieldSpec" in field) {
    if (field.fieldSpec.fieldType !== FieldType.BelongsTo) return false;
    const parentModelKey = getRootWebhookModelKey(field.modelKey);
    return field.fieldSpec.configuration?.relatedModelKey === parentModelKey;
  } else {
    if (field.fieldType !== FieldType.BelongsTo) return false;
    const parentModelKey = getRootWebhookModelKey(field.model.key);
    return field.configuration?.relatedModelKey === parentModelKey;
  }
};

export const isShopifyWebhookField = (field: FieldLike): boolean => {
  if (!fieldHasShopifyDominionConfig(field)) return false;
  const f = "fieldSpec" in field ? field.fieldSpec : field;

  if (f.fieldType == FieldType.ID) return true;

  // We always set the Shop field on webhooks
  if (isBelongsToShopModelField(f)) return true;

  // BelongsTo parent model fields are set from webhook context when processing nested data
  if (isBelongsToParentWebhookModel(field)) return true;

  const params = getParamsForFieldLike(field);
  if (!params) return false;

  const { apiVersion, modelKey, availableInWebhooks, frameworkVersionConstraint } = params;

  if (!availableInWebhooks) return false;

  const modelWebhooks = upsertWebhookTopicsForModelKey(modelKey, apiVersion, frameworkVersionConstraint);
  let availableInWebhookTopics: string[];
  if (Array.isArray(availableInWebhooks)) {
    availableInWebhookTopics = availableInWebhooks;
  } else {
    availableInWebhookTopics = Object.keys(availableInWebhooks);
  }

  return modelWebhooks.every((topic) => availableInWebhookTopics.includes(topic));
};

export const isBelongsToParentShopifyWebhookModelField = (field: ModelField) => {
  if (field.fieldType !== FieldType.BelongsTo) return false;

  const parentModelKey = getRootWebhookModelKey(field.model.key);

  return field.configuration.relatedModelKey === parentModelKey;
};

const fieldHasShopifyDominionConfig = (field: FieldLike) => {
  const f = "fieldSpec" in field ? field.fieldSpec : field;
  if (!f.contributorConfiguration?.[ShopifyDominion.id]) return false;

  // special case for Collection Type field
  // this erroneously had a contributor configuration for awhile when it shouldn't have
  if (f.key === "collection_type") return false;

  return true;
};

export const isShopifySyncOnlyField = (field: FieldLike) => {
  if (!fieldHasShopifyDominionConfig(field)) return false;

  return !isShopifyWebhookField(field);
};

export const isShopifySyncOnlyHasManyRelationship = (field: FieldLike) => {
  if (!fieldHasShopifyDominionConfig(field)) return false;

  const params = getParamsForFieldLike(field);
  if (!params) return false;
  const { apiVersion, modelKey, fieldType, configuration, frameworkVersionConstraint } = params;
  if (fieldType !== FieldType.HasMany) return false;

  const relatedModelKey = configuration?.relatedModelKey;
  if (!relatedModelKey) return false;
  const relatedMeta = shopifyModelMetadataForVersion(relatedModelKey, apiVersion, frameworkVersionConstraint);
  if (!relatedMeta?.parentKey || relatedMeta.parentKey !== modelKey) return false;
  return !isWebhookTriggeredModelKey(relatedModelKey, apiVersion, frameworkVersionConstraint);
};

export const isShopifyWebhookedHasManyRelationship = (field: FieldLike) => {
  if (!fieldHasShopifyDominionConfig(field)) return false;
  const params = getParamsForFieldLike(field);
  if (!params) return false;
  const { apiVersion, modelKey, fieldType, configuration, frameworkVersionConstraint } = params;
  if (fieldType !== FieldType.HasMany) return false;
  const relatedModelKey = configuration?.relatedModelKey;
  if (!relatedModelKey) return false;
  const relatedMeta = shopifyModelMetadataForVersion(relatedModelKey, apiVersion, frameworkVersionConstraint);
  if (!relatedMeta?.parentKey || relatedMeta.parentKey !== modelKey) return false;
  return isWebhookTriggeredModelKey(relatedModelKey, apiVersion, frameworkVersionConstraint);
};

export const isShopifyFieldSyncableOnWebhook = (
  field: FieldLike,
  shopifyApiVersion: ShopifyApiVersion,
  fieldApiIdentifier?: string // Only needed when field is a fieldSpec in metadata path
) => {
  const apiId = "fieldSpec" in field ? fieldApiIdentifier : field.apiIdentifier;
  const modelKey = "fieldSpec" in field ? field.modelKey : field.model.key;

  if (apiId && fieldHasCustomSyncOnWebhookBehavior(modelKey, apiId)) return true;

  if (!fieldHasShopifyDominionConfig(field)) return false;

  const frameworkVersionConstraint =
    "fieldSpec" in field ? field.frameworkVersionConstraint : getAppSettings(field).frameworkVersion.constraint;

  if (!isWebhookTriggeredModelKey(modelKey, shopifyApiVersion, frameworkVersionConstraint)) return false;

  const f = "fieldSpec" in field ? field.fieldSpec : field;
  if (FieldType.HasMany === f.fieldType) {
    return isShopifySyncOnlyHasManyRelationship(field);
  }

  return isShopifySyncOnlyField(field);
};

const getParamsForFieldLike = (
  field: FieldLike
): {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
  modelKey: string;
  fieldType: FieldType;
  configuration: any;
  availableInWebhooks: readonly string[] | Record<string, string> | null | undefined;
} | null => {
  let availableInWebhooks: readonly string[] | Record<string, string> | null | undefined;
  let modelKey: string;
  let apiVersion: ShopifyApiVersion;
  let fieldType: FieldType;
  let configuration: any;
  let frameworkVersionConstraint: string;

  if ("fieldSpec" in field) {
    apiVersion = field.apiVersion;
    modelKey = field.modelKey;
    fieldType = field.fieldSpec.fieldType;
    availableInWebhooks = field.fieldSpec.contributorConfiguration?.[ShopifyDominion.id]?.availableInWebhook;
    configuration = field.fieldSpec.configuration;
    frameworkVersionConstraint = field.frameworkVersionConstraint;
  } else {
    const root: LiveOrReadOnlyServerRoot = getGeneratingRoot(field);
    const settings = getAppSettings(root);
    const shopifyConnection = root.maybeGetShopifyConnection();
    if (!shopifyConnection) return null;
    apiVersion = (shopifyConnection.configuration as Instance<typeof ShopifyConnectionConfig>).shopifyApiVersion;
    modelKey = field.model.key;
    fieldType = field.fieldType;
    configuration = field.configuration;
    availableInWebhooks = field.contributorConfiguration?.[ShopifyDominion.id]?.availableInWebhook;
    frameworkVersionConstraint = settings.frameworkVersion.constraint;
  }

  return {
    apiVersion,
    modelKey,
    fieldType,
    configuration,
    availableInWebhooks,
    frameworkVersionConstraint,
  };
};

const LegacySyncOnWebhookFieldKeys: Record<
  string,
  | (({
      apiVersion,
      grantedScopes,
      webhookTopic,
      enabledModelKeys,
    }: {
      apiVersion: ShopifyApiVersion;
      grantedScopes?: string[];
      webhookTopic?: string | boolean;
      enabledModelKeys?: string[];
    }) => string[] | undefined)
  | undefined
> = {
  [ShopifyAppSubscriptionKey]: () =>
    // These fields are GraphQL-only and were previously fetched via preprocessPayload when the record didn't exist.
    // For legacy apps (fv < 1.6), continue fetching them on webhook to maintain backwards compatibility.
    ["currentPeriodEnd", "returnUrl", "test", "trialDays", "lineItems"],
  [ShopifyCustomerKey]: ({ apiVersion }) =>
    apiVersion >= "2025-01"
      ? // in 2025-01 we were enriching missing webhook payloads; afterwards we were fetching the fields
        // but not all of the fields has rest field keys any more;
        [
          "tags",
          "amountSpent",
          "lastOrder",
          "smsMarketingConsent",
          "emailMarketingConsent",
          ...(apiVersion === "2025-01" ? ["numberOfOrders"] : []),
        ]
      : undefined,
  [ShopifyCollectionKey]: () => ["description", "feedback", "legacyResourceId", "productsCount", "seo"],
  [ShopifyPaymentTermsKey]: ({ grantedScopes }) => {
    const fields = [];
    if (grantedScopes?.includes("read_orders")) {
      fields.push("order");
    }
    if (grantedScopes?.includes("read_draft_orders")) {
      fields.push("draftOrder");
    }
    return fields.length > 0 ? fields : undefined;
  },
  [ShopifyCompanyKey]: ({ apiVersion }) =>
    apiVersion >= "2025-04"
      ? // in 2025-04 we were enriching missing webhook payloads with a GraphQL call;
        // these fields are now sync-only but existing apps should continue to fetch them on webhook
        ["contactCount", "contactsCount", "ordersCount", "locationsCount", "lifetimeDuration", "totalSpent", "defaultRole", "mainContact"]
      : undefined,
  [ShopifyCompanyContactKey]: ({ apiVersion }) =>
    apiVersion >= "2025-04"
      ? // in 2025-04 we were enriching missing webhook payloads with a GraphQL call;
        // lifetimeDuration is sync-only but existing apps should continue to fetch it on webhook
        ["lifetimeDuration"]
      : undefined,
  [ShopifyCompanyLocationKey]: ({ apiVersion }) =>
    apiVersion >= "2025-04"
      ? // in 2025-04 we were enriching missing webhook payloads with a GraphQL call;
        // these fields are now sync-only but existing apps should continue to fetch them on webhook
        // note: hasTimelineComment is the graphQLFieldKey for the timelineComment model field
        ["market", "currency", "ordersCount", "totalSpent", "hasTimelineComment"]
      : undefined,
  [ShopifyDiscountKey]: ({ apiVersion, enabledModelKeys }) => {
    if (apiVersion < "2024-01") return undefined;

    // in 2024-01 we were enriching discount webhook payloads with a GraphQL call;
    // these fields are now sync-only but existing apps should continue to fetch them on webhook
    const fields = [
      "appDiscountType",
      "combinesWith",
      "discountClass",
      "endsAt",
      "startsAt",
      "errorHistory",
      "asyncUsageCount",
      "shortSummary",
      "summary",
      "minimumRequirement",
      "usesPerOrderLimit",
      "appliesOncePerCustomer",
      "hasTimelineComment",
      "recurringCycleLimit",
      "shareableUrls",
      "totalSales",
      "usageLimit",
      "codesCount",
      "customerGets",
      "customerBuys",
      "appliesOnOneTimePurchase",
      "appliesOnSubscription",
      "destinationSelection",
      "maximumShippingPrice",
    ];

    // Include the codes (redeem codes) relationship if the model is enabled
    if (enabledModelKeys?.includes(ShopifyDiscountRedeemCodeKey)) {
      fields.push(ShopifyDiscountToDiscountRedeemCodesKey);
    }

    return fields;
  },
  [ShopifyOrderKey]: ({ webhookTopic }) => {
    if (webhookTopic && webhookTopic === "orders/risk_assessment_changed") {
      return ["risk"];
    }

    return undefined;
  },
  [ShopifyMarketKey]: ({ apiVersion, enabledModelKeys, webhookTopic }) => {
    // For 2025-04+, webPresences are synced as a graphql sublist, so skip syncing here for REST syncs
    // But still sync for webhooks (backwards compatibility)
    // webhookTopic can be a string (actual webhook topic) or boolean true (for converge path)
    if (apiVersion >= "2025-04" && !webhookTopic) return undefined;

    if (!enabledModelKeys) return undefined;

    const fields = [];
    if (enabledModelKeys.includes(ShopifyMarketRegionKey)) {
      fields.push("regions");
    }
    if (enabledModelKeys.includes(ShopifyMarketWebPresenceKey)) {
      if (apiVersion <= "2023-07") {
        fields.push("webPresence");
      } else {
        fields.push("webPresence", "webPresences");
      }
    }
    return fields.length > 0 ? fields : undefined;
  },
  [ShopifySellingPlanGroupKey]: ({ apiVersion }) =>
    apiVersion >= "2025-04"
      ? // in 2025-04 we were enriching missing webhook payloads with a GraphQL call;
        // createdAt is sync-only but existing apps should continue to fetch it on webhook
        ["createdAt"]
      : undefined,
  [ShopifySubscriptionContractKey]: ({ enabledModelKeys, grantedScopes }) => {
    const fields = [
      "lastBillingAttemptErrorType",
      "appAdminUrl",
      "deliveryMethod",
      "deliveryPrice",
      "lastPaymentStatus",
      "nextBillingDate",
      "note",
      "createdAt",
      "updatedAt",
    ];

    if (grantedScopes?.includes("read_orders") && enabledModelKeys?.includes(ShopifyOrderKey)) {
      fields.push("originOrder");
    }

    if (grantedScopes?.includes("read_customer_payment_methods") && enabledModelKeys?.includes(ShopifyCustomerPaymentMethodKey)) {
      fields.push("customerPaymentMethod");
    }
    return fields.length > 0 ? fields : undefined;
  },
  [ShopifyCustomerPaymentMethodKey]: () => {
    return ["instrument", "revokedAt", "revokedReason", "customer"];
  },
  [ShopifyFulfillmentOrderKey]: ({ apiVersion, grantedScopes, enabledModelKeys }) => {
    // Fulfillment orders switched to GraphQL in 2025-04; before that we used REST which fetched all fields
    // In legacy mode (pre 1.6), we fetched ALL fields from the API on webhook since the webhook payload
    // only contains the fulfillment order ID. These are all the GraphQL fields we were fetching.
    if (apiVersion < "2025-04") {
      return undefined;
    }

    const fields = [
      // Core fields
      "assignedLocation",
      "assignedLocationId", // this is skipped during the actual selection
      "fulfillAt",
      "fulfillBy",
      "internationalDuties",
      "supportedActions",
      "createdAt",
      "updatedAt",
      "orderName",
      "orderProcessedAt",
      "deliveryMethod",
      "destination",
      "fulfillmentHolds",
      "requestStatus",
    ];

    if (grantedScopes?.includes("read_orders") && enabledModelKeys?.includes(ShopifyOrderKey)) {
      fields.push("order");
    }

    if (enabledModelKeys?.includes(ShopifyFulfillmentOrderLineItemKey)) {
      fields.push(ShopifyFulfillmentOrderToFulfillmentOrderLineItemsKey);
    }

    return fields.length > 0 ? fields : undefined;
  },
};

export const legacySyncOnWebhookFields = ({
  modelKey,
  apiVersion,
  grantedScopes,
  webhookTopic,
  enabledModelKeys,
}: {
  modelKey: string;
  apiVersion: ShopifyApiVersion;
  grantedScopes?: string[];
  webhookTopic?: string | boolean;
  enabledModelKeys?: string[];
}): string[] | undefined => {
  return LegacySyncOnWebhookFieldKeys[modelKey]?.({
    apiVersion,
    grantedScopes,
    webhookTopic,
    enabledModelKeys,
  });
};

const CustomSyncOnWebhookBehaviorFields = {
  [ShopifyProductKey]: ["variants", "media"],
  [ShopifyProductVariantKey]: ["media"],
  [ShopifyOrderKey]: ["risk"],
  [ShopifyDiscountKey]: [
    "codes",
    "customerGetsProducts",
    "customerGetsProductVariants",
    "customerGetsCollections",
    "customerBuysProducts",
    "customerBuysProductVariants",
    "customerBuysCollections",
  ],
  // Special case: ShopifyFulfillmentOrder is missing data for itself in it's own webhooks
  [ShopifyFulfillmentOrderKey]: ["id"],
} as const;

/** Returns true if the model has fields with custom sync-on-webhook behavior (i.e., is a parent model in CustomSyncOnWebhookBehaviorFields) */
export const modelHasCustomSyncOnWebhookBehaviorFields = (modelKey: string) => {
  return modelKey in CustomSyncOnWebhookBehaviorFields;
};

export const fieldHasCustomSyncOnWebhookBehavior = (modelKeyOrField: string | ModelField, apiIdentifier?: string) => {
  const modelKey = typeof modelKeyOrField === "string" ? modelKeyOrField : modelKeyOrField.model.key;
  const fieldApiId = typeof modelKeyOrField === "string" ? apiIdentifier : modelKeyOrField.apiIdentifier;

  const fields = CustomSyncOnWebhookBehaviorFields[modelKey as keyof typeof CustomSyncOnWebhookBehaviorFields] as
    | readonly string[]
    | undefined;

  return !!fields?.includes(fieldApiId!);
};

const CustomSyncOnWebhookBehaviorFieldsForTopic: Record<string, Record<string, string[]>> = {
  [ShopifyOrderKey]: {
    "orders/risk_assessment_changed": ["risk"],
  },
};

/**
 * Checks if a field could potentially have topic-specific custom sync behavior
 * (i.e., is it one of the fields that are configured for topic-specific behavior on this model).
 */
export const fieldCouldHaveTopicSpecificBehavior = (field: ModelField): boolean => {
  const modelKey = field.model.key;
  const topicFieldsMap = CustomSyncOnWebhookBehaviorFieldsForTopic[modelKey];
  if (!topicFieldsMap) return false;

  const fieldIdentifier = field.apiIdentifier;
  // Check if this field appears in any topic's field list
  return Object.values(topicFieldsMap).some((fields) => fields.includes(fieldIdentifier));
};

/**
 * Checks if a field has custom sync on webhook behavior for a specific webhook topic.
 * Use this when you have webhook topic context (e.g., in the enricher).
 */
export const fieldHasCustomSyncOnWebhookBehaviorForTopic = (
  field: ModelField,
  apiVersion: ShopifyApiVersion,
  webhookTopic?: string
): boolean => {
  const modelKey = field.model.key;
  const topicFieldsMap = CustomSyncOnWebhookBehaviorFieldsForTopic[modelKey];
  if (!topicFieldsMap || !webhookTopic) return false;

  const fieldKeys = topicFieldsMap[webhookTopic];
  return !!fieldKeys?.includes(field.apiIdentifier);
};
